// useBriefDraftLiveStream — subscriber for `brief-draft:stream` events emitted
// by runBriefDraftPass. Unlike useCanvasStream (AI Chat "write to canvas"),
// the server-side edge function has already created the draft row, so this
// hook does NOT create revisions. It just renders incoming markdown into the
// open Draft Canvas when the briefId matches.

import { useEffect, useRef, useState } from "react";
import { composerBus } from "@/lib/composerBus";
import { markdownToTiptapDoc } from "@/lib/markdownToTiptap";

export type BriefDraftLiveStreamState = {
  active: boolean;
  doc: unknown;
  label: string | null;
};

export function useBriefDraftLiveStream(briefId: string | null | undefined): BriefDraftLiveStreamState {
  const [state, setState] = useState<BriefDraftLiveStreamState>({
    active: false,
    doc: null,
    label: null,
  });
  const lastGoodDocRef = useRef<unknown>(null);

  useEffect(() => {
    if (!briefId) return;
    return composerBus.subscribe("brief-draft:stream", (payload) => {
      if (payload.briefId !== briefId) return;
      if (payload.phase === "start") {
        lastGoodDocRef.current = null;
        setState({
          active: true,
          doc: { type: "doc", content: [{ type: "paragraph" }] },
          label: payload.label ?? "Drafting…",
        });
        return;
      }
      if (payload.phase === "chunk") {
        let doc: unknown;
        try {
          doc = markdownToTiptapDoc(payload.markdown);
          lastGoodDocRef.current = doc;
        } catch {
          doc = lastGoodDocRef.current ?? {
            type: "doc",
            content: [{ type: "paragraph", content: [{ type: "text", text: payload.markdown }] }],
          };
        }
        setState((prev) => (prev.active ? { ...prev, doc } : prev));
        return;
      }
      if (payload.phase === "end") {
        // Leave the final rendered doc visible until the parent refresh swaps
        // in the persisted draft. Reset `active` so the editor unlocks.
        setState({ active: false, doc: null, label: null });
        lastGoodDocRef.current = null;
      }
    });
  }, [briefId]);

  return state;
}
